import Core from 'handsontable/core';
import { CellType, CellValueDetailedType } from 'hyperformula/es';
import { ContentState, convertFromRaw } from 'draft-js';
import { getRenderCellValueFromNormalisedComputedCellValue } from '../../../../common/table/utils/tableCellFormattingUtils';
import { getNumericDateTimeStringFromCellValue } from '../../../../common/table/utils/tableInputDateTimeUtils';
import { getDefaultTypeObject } from '../../../../common/table/utils/tableCellTypeUtils';
import {
    CellData,
    CellFormulaInfo,
    DateTimeTypeObject,
    HotComputedCellValue,
} from '../../../../common/table/TableTypes';
import {
    CellTypeNames,
    DateStringFormatOptions,
    FormatOptions,
    TimeStringFormatOptions,
} from '../../../../common/table/CellTypeConstants';
import applyInlineStylesThroughoutContent from '../../../components/editor/customRichUtils/applyInlineStylesThroughoutContent';
import hyperFormulaInstance from '../manager/hyperFormulaInstance';
import { isFormula } from './tableFormulaUtils';
import { isBoolean, isNil, isNumber } from 'lodash';

/**
 * Convert render value calculated by HyperFormula to a more readable value.
 *
 * This covers cases such as:
 * - If formula is calculated to be a date by HyperFormula, it will be represented in numbers of days from X date,
 *    we want to render this in a readable format.
 */
export const normaliseComputedCellValue = (
    hotComputedCellValue: HotComputedCellValue,
    locale: string,
    cellFormulaInfo: Partial<CellFormulaInfo> = {},
): HotComputedCellValue => {
    const { hfType, hfDetailedType } = cellFormulaInfo;

    if (
        isNumber(hotComputedCellValue) &&
        hfType === CellType.FORMULA &&
        (hfDetailedType === CellValueDetailedType.NUMBER_DATE ||
            hfDetailedType === CellValueDetailedType.NUMBER_DATETIME ||
            hfDetailedType === CellValueDetailedType.NUMBER_TIME)
    ) {
        const dateTime = hyperFormulaInstance.numberToDateTime(hotComputedCellValue);

        let year = 'year' in dateTime ? dateTime.year : undefined;
        let month = 'month' in dateTime ? dateTime.month : undefined;
        let day = 'day' in dateTime ? dateTime.day : undefined;
        const hours = 'hours' in dateTime ? dateTime.hours : undefined;
        const minutes = 'minutes' in dateTime ? dateTime.minutes : undefined;
        const seconds = 'seconds' in dateTime ? dateTime.seconds : undefined;

        if (hfDetailedType === CellValueDetailedType.NUMBER_TIME) {
            const today = new Date();
            day = today.getDate();
            month = today.getMonth() + 1;
            year = today.getFullYear();
        }

        if (year === undefined || month === undefined) return hotComputedCellValue;

        const date = new Date(year, month - 1, day, hours, minutes, seconds);

        const hasTime =
            hfDetailedType === CellValueDetailedType.NUMBER_DATETIME ||
            hfDetailedType === CellValueDetailedType.NUMBER_TIME;

        const epochTime = date.getTime();
        const dateTypeObject = {
            ...getDefaultTypeObject<DateTimeTypeObject>(CellTypeNames.DATE_TIME),

            [FormatOptions.HAS_TIME]: hasTime,
            [FormatOptions.HAS_SECONDS]: hasTime,
            [FormatOptions.TIME_FORMAT]: TimeStringFormatOptions.TWENTY_FOUR_HOUR_SECONDS,

            ...(hfDetailedType === CellValueDetailedType.NUMBER_TIME
                ? { [FormatOptions.DATE_FORMAT]: DateStringFormatOptions.NONE }
                : {}),
        };

        const numericDateTimeString = getNumericDateTimeStringFromCellValue(
            epochTime.toString(),
            dateTypeObject,
            locale,
        );
        if (numericDateTimeString) return numericDateTimeString;
    }

    return hotComputedCellValue;
};

/**
 * Returns a hotComputedCellValue with formula date results converted to numeric strings
 * Useful for knowing what type a cell will be rendered as
 */
export const getNormalisedComputedCellValueFromHot = (
    elementId: string,
    row: number | undefined,
    col: number | undefined,
    hot: Core,
) => {
    if (isNil(row) || isNil(col)) return null;

    const hotComputedCellValue = hot.getDataAtCell(row, col);
    const cellValue = hot.getCellMeta(row, col)?.cellData?.value;
    if (!isFormula(cellValue)) return hotComputedCellValue;

    const sheet = hyperFormulaInstance.getSheetId(elementId);
    if (isNil(sheet)) return hotComputedCellValue;

    const cellFormulaInfo = {
        hfType: hyperFormulaInstance.getCellType({ sheet, row, col }),
        hfDetailedType: hyperFormulaInstance.getCellValueDetailedType({ sheet, row, col }),
    };

    // @ts-ignore - custom property
    const { locale } = hot.milanoteProps;

    return normaliseComputedCellValue(hotComputedCellValue, locale, cellFormulaInfo);
};

/**
 * Returns the renderCellValue to be displayed in the cell
 * @param cellData
 * @param hotComputedCellValue - if this value is not passed in, formulas will NOT be resolved
 * @param cellFormulaInfo
 * @param locale
 */
export const getCellRenderValue = (
    cellData: CellData,
    locale: string,
    hotComputedCellValue: HotComputedCellValue = null,
    cellFormulaInfo: Partial<CellFormulaInfo> = {},
): string => {
    const value = hotComputedCellValue ?? cellData.value;
    const normalisedComputedCellValue = normaliseComputedCellValue(value, locale, cellFormulaInfo);

    if (isNil(normalisedComputedCellValue)) return '';
    if (isBoolean(normalisedComputedCellValue)) return normalisedComputedCellValue.toString();

    return getRenderCellValueFromNormalisedComputedCellValue(cellData, locale, normalisedComputedCellValue).toString();
};

/**
 * Gets the Draft.JS content state to be rendered in the cell.
 *
 * TODO-TABLES - Ask Marisa to explain this logic
 */
export const getCellRenderContentState = (
    cellData: CellData,
    locale: string,
    hotComputedCellValue?: HotComputedCellValue,
    cellFormulaInfo: Partial<CellFormulaInfo> = {},
): ContentState | null => {
    if (isNil(hotComputedCellValue) && !cellData.draftContent) return null;

    let contentState: ContentState;

    if (cellData.draftContent) {
        contentState = convertFromRaw(cellData.draftContent);
    } else {
        const cellRenderValue = getCellRenderValue(cellData, locale, hotComputedCellValue, cellFormulaInfo);
        contentState = ContentState.createFromText(cellRenderValue);
    }

    return applyInlineStylesThroughoutContent(contentState, cellData.textStyle);
};
